import {
	Button,
	Card,
	EmptyState,
	Icon,
	type IconName,
	IconTile,
	icons,
	ListRow,
	Skeleton,
} from "@iec-id/ui";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AuthBrandBar } from "@/components/signin/auth-card";
import { useLegalT } from "./i18n";
import { usePublicLegalManifest } from "./public-legal";

/**
 * Список документов правового центра, открытый гостю — `/legal`.
 *
 * Ключи берутся из манифеста, а не из локального перечисления: документ,
 * добавленный Legal Center'ом, обязан появиться здесь без релиза фронта
 * (инвариант №12 — манифест первичен).
 */
/** Иконка приходит из манифеста строкой — неизвестное имя даёт «Scroll». */
function documentIcon(name: string): IconName {
	return name in icons ? (name as IconName) : "Scroll";
}

export function PublicDocuments() {
	const t = useLegalT();
	const { i18n } = useTranslation();
	const manifest = usePublicLegalManifest(i18n.language);

	return (
		<Card padding="auth" gap="row" className="w-full max-w-[760px]">
			{/* Правовой центр открыт гостю и часто открывается по прямой ссылке
			    из письма или из чужого сервиса — знак отвечает на «чей это
			    документ», а переключатель языка нужен здесь даже больше, чем на
			    входе: документы существуют в двух локалях. */}
			<AuthBrandBar />
			<header className="flex flex-col gap-1.5">
				<h1 className="t-section">{t("index.title")}</h1>
				<span className="t-meta text-fg-muted">{t("index.hint")}</span>
			</header>

			{manifest.isPending ? (
				<div className="flex flex-col gap-2">
					{Array.from({ length: 5 }, (_, index) => (
						<Skeleton key={index} height={44} radius="control" />
					))}
				</div>
			) : null}

			{manifest.isError ? (
				<EmptyState
					icon={<Icon name="TriangleAlert" size={32} />}
					title={t("index.errorTitle")}
					action={
						<Button variant="second" onClick={() => void manifest.refetch()}>
							{t("reader.retry")}
						</Button>
					}
				/>
			) : null}

			{manifest.data ? (
				<div className="flex flex-col">
					{manifest.data.documents.map((item) => (
						<Link key={item.key} to="/legal/$key" params={{ key: item.key }}>
							<ListRow
								density="default"
								interactive
								icon={
									<IconTile>
										<Icon name={documentIcon(item.icon)} />
									</IconTile>
								}
								title={item.title}
								subtitle={`v${item.version}`}
								trailing={<span className="t-meta text-fg-muted">{t("index.open")}</span>}
							/>
						</Link>
					))}
				</div>
			) : null}
		</Card>
	);
}
